import * as React from 'react';
import { Link2 } from 'lucide-react';
import { Spinner } from '@/components/ui/spinner';
import { ErrorMessage } from '@/components/ui/error-message';
import { RecordRow, linkishClass, useToast } from '@/components/ui/workspace';
import { useDiscoverySettingsQuery } from '@/lib/api/discovery-settings';
import {
  extendLink,
  useDiscoveryWrite,
  useQuestionnairePreviewQuery,
  type RunLink,
} from '@/lib/api/discovery-sessions';
import { errorText } from './errors';
import { longDay, shortDay } from './sessions-logic';

/**
 * Secure links and the respondent preview, shared by an interview's
 * questionnaire step and the department round (E12-F08, rule 16).
 */

/** The firm's link lifetime in days (Decision 54.26), 14 until it loads. */
export function useLinkDays(): number {
  return useDiscoverySettingsQuery().data?.link_ttl_days ?? 14;
}

const LINK_STATUS: Record<string, string> = {
  live: 'not answered yet',
  completed: 'answered',
  expired: 'expired',
  revoked: 'revoked',
};

/** "Extend 14 days" for the engagement lead (or a firm admin); the API checks it too. */
export function ExtendLinkButton({
  engagementId,
  link,
  canExtend,
  who,
}: {
  engagementId: string;
  link: RunLink;
  canExtend: boolean;
  who: string;
}) {
  const { toast } = useToast();
  const days = useLinkDays();
  const extend = useDiscoveryWrite(engagementId, () => extendLink(engagementId, link.id));
  return (
    <button
      type="button"
      className={linkishClass()}
      disabled={!canExtend || extend.isPending}
      title={canExtend ? undefined : 'Only the engagement lead can extend a link'}
      onClick={() =>
        extend.mutate(undefined, {
          onSuccess: (r) =>
            toast({
              message: `Link extended to ${longDay(r.expires_at)}. ${who} is sent a note with the new date.`,
            }),
          onError: (e) => toast({ message: errorText(e), tone: 'critical' }),
        })
      }
    >
      Extend {days} days
    </button>
  );
}

export function LinkRow({
  engagementId,
  link,
  who,
  canExtend,
}: {
  engagementId: string;
  link: RunLink;
  who: string;
  canExtend: boolean;
}) {
  const days = useLinkDays();
  const live = link.status === 'live';
  return (
    <>
      <RecordRow icon={<Link2 className="h-3.5 w-3.5" />} muted={!live} className="mt-2.5">
        <span className="min-w-0 flex-1">
          Secure link to {who} · sent {shortDay(link.issued_at)} ·{' '}
          {LINK_STATUS[link.status] ?? link.status}
        </span>
        {live ? (
          <>
            <span className="text-meta text-ink-500">Expires {longDay(link.expires_at)}</span>
            <ExtendLinkButton
              engagementId={engagementId}
              link={link}
              canExtend={canExtend}
              who={who}
            />
          </>
        ) : null}
      </RecordRow>
      <div className="text-meta text-ink-500">
        Links last {days} days, set in Firm settings. Only the engagement lead can extend one
        {link.extended_at ? `; extended ${longDay(link.extended_at)}` : ''}.
      </div>
    </>
  );
}

/**
 * What a respondent sees, shown in place (the client page itself is
 * T-0191). The same payload as the secure-link session; nothing is created.
 */
export function QuestionnairePreview({
  engagementId,
  questionnaireId,
  department = '',
}: {
  engagementId: string;
  questionnaireId: string;
  department?: string;
}) {
  const q = useQuestionnairePreviewQuery(engagementId, questionnaireId, department, true);
  if (q.error) return <ErrorMessage error={q.error} />;
  if (!q.data) {
    return (
      <div className="flex items-center gap-2 text-meta text-ink-500">
        <Spinner /> Loading the preview…
      </div>
    );
  }
  const p = q.data.payload;
  return (
    <section
      aria-label="Preview: what the respondent sees"
      className="mt-3 rounded-card border border-ink-100 bg-paper-50 px-5 py-4"
    >
      <div className="text-meta text-ink-500">Preview · what the respondent sees</div>
      <div className="mt-1 text-h3 font-semibold text-ink-900">{p.title}</div>
      <div className="text-meta text-ink-500">
        {[p.audience_line, p.minutes ? `about ${p.minutes} minutes` : null]
          .filter(Boolean)
          .join(' · ')}
      </div>
      <ol className="m-0 mt-3 list-none p-0">
        {p.questions.map((x) => (
          <li key={x.ordinal} className="border-t border-ink-100 py-2 text-body-sm text-ink-900">
            <span className="mr-2 font-mono text-meta text-ink-500">{x.ordinal}.</span>
            {x.prompt}
            <span className="ml-2 text-meta text-ink-500">{ANSWER_HINT[x.answer_type]}</span>
            {x.options.length > 0 ? (
              <span className="mt-1 block text-meta text-ink-500">{x.options.join(' · ')}</span>
            ) : null}
          </li>
        ))}
      </ol>
    </section>
  );
}

const ANSWER_HINT: Record<string, string> = {
  text: 'Written answer',
  number: 'A number or a range',
  rating: 'Rating, 1 to 5',
  single: 'Pick one',
  multiple: 'Pick any',
};
